import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { login } from '../../api';
import { saveToken, saveRole, isAuthed } from '../../auth';
import styles from './LoginPage.module.scss';

import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Alert,
    IconButton,
    InputAdornment,
    Avatar,
} from '@mui/material';

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export default function LoginPage() {

    const navigate = useNavigate();
    const location = useLocation();

    const urlFrom = new URLSearchParams(location.search).get('from');
    const from = urlFrom || location.state?.from || '/home';

    const [email, setEmail] = useState('');
    const [pass, setPass] = useState('');
    const [otp, setOtp] = useState('');

    const [showPass, setShowPass] = useState(false);
    const [err, setErr] = useState('');
    const [hint, setHint] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {

        if (isAuthed()) {
            navigate(from, { replace: true });
        } else {
            const last = localStorage.getItem('admin.lastEmail');
            if (last && !email) setEmail(last);
        }

        // eslint-disable-next-line
    }, []);

    const disabled =
        busy ||
        !email.trim() ||
        !pass;

    async function onSubmit(e) {

        e.preventDefault();
        if (disabled) return;

        setErr('');
        setHint('');
        setBusy(true);

        try {

            localStorage.setItem(
                'admin.lastEmail',
                email.trim()
            );

            const res = await login(
                email.trim(),
                pass,
                otp.trim() || undefined
            );

            if (!res?.accessToken) {
                throw new Error('No token received');
            }

            saveToken(res.accessToken);
            if (res.role) saveRole(res.role);

            navigate(from, { replace: true });

        } catch (e) {

            const msg = String(e?.message || '');
            const payload = e?.payload || {};

            if (payload.no2FA) {
                setErr('2FA не налаштовано. Зверніться до головного адміністратора.');
            } else if (payload.requires2FA) {
                setHint('Введіть код з Authenticator для входу');
                setErr('');
            } else if (/401|Invalid credentials/i.test(msg)) {
                setErr('Невірний email або пароль');
            } else if (/Invalid 2FA/i.test(msg)) {
                setErr('Невірний код 2FA');
            } else {
                setErr(msg || 'Login failed');
            }

        } finally {
            setBusy(false);
        }
    }

    return (
        <div className={styles.screen}>

            <div className={styles.blobA} />
            <div className={styles.blobB} />

            <Card
                component="form"
                className={styles.card}
                onSubmit={onSubmit}
                autoComplete="on"
                elevation={0}
            >

                <CardContent sx={{ p: 0 }}>

                    <div className={styles.brand}>

                        <Avatar
                            variant="rounded"
                            className={styles.logo}
                        >
                            B
                        </Avatar>

                        <div className={styles.brandText}>
                            <h1 className={styles.title}>
                                BSP Admin
                            </h1>

                            <p className={styles.subtitle}>
                                Увійдіть, щоб керувати продукцією
                            </p>
                        </div>

                    </div>

                    {err && (
                        <Alert
                            severity="error"
                            icon={<ErrorOutlineIcon fontSize="small" />}
                            className={styles.alert}
                        >
                            {err}
                        </Alert>
                    )}

                    {hint && !err && (
                        <Alert
                            severity="info"
                            className={styles.alert}
                        >
                            {hint}
                        </Alert>
                    )}

                    <div className={styles.form}>

                        {/* EMAIL */}

                        <label
                            className={styles.label}
                            htmlFor="email"
                        >
                            Email
                        </label>

                        <TextField
                            id="email"
                            type="email"
                            placeholder="admin@bsp-group.com"
                            value={email}
                            onChange={(e) =>
                                setEmail(
                                    e.target.value.replace(/\s+$/, '')
                                )
                            }
                            autoFocus
                            required
                            fullWidth
                            size="small"
                            variant="outlined"
                            autoComplete="username email"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailOutlinedIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* PASSWORD */}

                        <label
                            className={styles.label}
                            htmlFor="password"
                        >
                            Пароль
                        </label>

                        <TextField
                            id="password"
                            type={showPass ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={pass}
                            onChange={(e) => setPass(e.target.value)}
                            required
                            fullWidth
                            size="small"
                            variant="outlined"
                            autoComplete="current-password"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlinedIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            edge="end"
                                            onMouseDown={(e) =>
                                                e.preventDefault()
                                            }
                                            onClick={() =>
                                                setShowPass((v) => !v)
                                            }
                                            disabled={busy}
                                            size="small"
                                        >
                                            {showPass
                                                ? <VisibilityOffIcon />
                                                : <VisibilityIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* 2FA */}

                        <label
                            className={styles.label}
                            htmlFor="otp"
                        >
                            2FA код
                        </label>

                        <TextField
                            id="otp"
                            type="text"
                            placeholder="123456"
                            value={otp}
                            onChange={(e) =>
                                setOtp(
                                    e.target.value.replace(/\D/g, '')
                                )
                            }
                            fullWidth
                            size="small"
                            variant="outlined"
                            autoComplete="one-time-code"
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 6,
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <KeyOutlinedIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            className={styles.btn}
                            type="submit"
                            disabled={disabled}
                            fullWidth
                            variant="contained"
                            size="medium"
                        >

                            {busy && (
                                <span className={styles.spinner} />
                            )}

                            Увійти

                        </Button>

                        <Typography className={styles.muted}>
                            Введіть email, пароль та код з Authenticator
                        </Typography>

                    </div>

                </CardContent>

            </Card>

        </div>
    );
}
